import React from 'react';
import { getAttributes } from '../utils';
import { Box } from '../../../shared-components/Box';

/**
 * Simplified Table component for the viewer.
 * Assumes `item` contains:
 * - children: array of row IDs (first is header)
 * - table: lookup of row and cell data by ID, each with `.children` (cell IDs), `.colStyle`, and `.html` for cell content
 *
 * `totalColumns` and `containerAttributes` come from the parent layout.
 */
export const Table = ({ item, totalColumns }) => {
  // Compute wrapper attributes from utils
  const componentAttributes = getAttributes({
    column: { ...item, component: 'table' },
    length: totalColumns,
    type: 'component',
  });

  const headId = item?.data?.children?.[0];
  const head = hydrate(item, headId);
  const rows = item?.data?.children?.slice(1).map((id) => {
    return hydrate(item, id);
  });
  return (
    <Box css={tableStyle} {...componentAttributes}>
      <table>
        <colgroup>
          {head?.children?.map((data) => {
            return <col style={data?.colStyle || {}} />;
          })}
        </colgroup>
        <thead style={getTableStyle(item, 'thead')}>
          <tr>
            {head?.children?.map((data, idx) => {
              return (
                <th key={data?.id} style={getTableStyle(item, 'th')} className={`row-0col-${idx} text-h4-component`}>
                  <Box
                    dangerouslySetInnerHTML={{ __html: data?.html || '' }}
                    css={`
                      width: 100%;
                    `}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows?.map((data, rowIdx) => (
            <tr key={data?.id} style={getTableStyle(item, 'tr')}>
              {data?.children.map((column, idx) => (
                <td
                  key={column?.id}
                  style={getTableStyle(item, 'td')}
                  className={`row-${rowIdx + 1} col-${idx} text-body-component`}
                >
                  <Box
                    dangerouslySetInnerHTML={{ __html: column?.html || '' }}
                    css={`
                      width: 100%;
                    `}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </Box>
  );
};

export const getCell = (properties, id) => {
  return properties?.data?.table?.[id] || {};
};

export const hydrate = (properties, id) => {
  const data = properties?.data?.table?.[id];
  const children = data?.children?.map((childId) => {
    const data = properties?.data?.table?.[childId];
    return data;
  });
  return { data, children };
};

export const styles = {
  thead: {},
  th: {
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '8px',
    paddingRight: '8px',
    textAlign: 'left',
    backgroundColor: 'var(--button-background-color)',
    color: 'var(--button-text-color)',
  },
  td: {
    paddingTop: '8px',
    paddingBottom: '8px',
    paddingLeft: '8px',
    paddingRight: '8px',
    color: 'var(--text-body)',
    backgroundColor: 'rgba(0, 0, 0, 0)',
    textAlign: 'left',
    borderBottom: '1px solid var(--accent-color-4)',
  },
  tr: {},
};

export const tableStyle = `

  width: 100%;

  table, thead, tbody, tr, th, td {
    margin: 0;
    padding: 0;
    border: none;
    box-sizing: border-box;
    transition: padding .3s ease;
  }

  table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
    table-layout: fixed;
  }

  th *, td * {
    margin: 0;
    padding: 0;
  }
  
`;

export const getTableStyle = (tableProperties, element, property = '') => {
  if (property) {
    const defaultStyles = styles?.[element]?.[property];
    const customStyles = tableProperties?.data?.styles?.[element]?.[property];
    return customStyles || defaultStyles;
  } else {
    return {
      ...styles?.[element],
      ...tableProperties?.data?.styles?.[element],
    };
  }
};
